// POWER HOUR
// Once per UTC day there is a deterministic 1-hour window where the
// WALLET COST of any time spent (posting, voting, following) is halved.
// The resulting post/vote still lasts the full requested duration —
// it just costs you half.
//
// The hour is picked by a stable hash of the UTC date string, so every
// user on earth experiences the same Power Hour simultaneously. This
// creates a synchronized daily rush (Wordle-style scarcity + slot-machine
// variable reward: you never know what hour it'll land on each day).

const POWER_HOUR_DISCOUNT = 0.5;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function utcDateKey(d: Date): string {
  return d.toISOString().slice(0, 10); // "YYYY-MM-DD"
}

/** Which UTC hour (0-23) is Power Hour for the given date? */
export function getPowerHourForDate(d: Date): number {
  return hashString(utcDateKey(d)) % 24;
}

export interface PowerHourStatus {
  isActive: boolean;
  start: Date;        // Start of the Power Hour that is current-or-next
  end: Date;          // End of that window
  msUntilStart: number; // 0 if active
  msUntilEnd: number;   // > 0
}

/**
 * Returns the status of the current-or-next Power Hour.
 * If active: isActive=true, msUntilStart=0, msUntilEnd=time left.
 * If pending: isActive=false, msUntilStart=time to go, msUntilEnd=time until it ends.
 */
export function getPowerHourStatus(now: Date = new Date()): PowerHourStatus {
  const nowHour = now.getUTCHours();
  const todayHour = getPowerHourForDate(now);

  let start: Date;
  if (nowHour < todayHour) {
    // Today's hasn't happened yet
    start = new Date(now);
    start.setUTCHours(todayHour, 0, 0, 0);
  } else if (nowHour === todayHour) {
    // We're in it
    start = new Date(now);
    start.setUTCHours(todayHour, 0, 0, 0);
  } else {
    // Today's has already passed — report tomorrow's
    const tomorrow = new Date(now);
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const tomorrowHour = getPowerHourForDate(tomorrow);
    start = new Date(tomorrow);
    start.setUTCHours(tomorrowHour, 0, 0, 0);
  }

  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const isActive = now >= start && now < end;
  const msUntilStart = isActive ? 0 : Math.max(0, start.getTime() - now.getTime());
  const msUntilEnd = Math.max(0, end.getTime() - now.getTime());

  return { isActive, start, end, msUntilStart, msUntilEnd };
}

/** Multiplier to apply to any wallet cost right now (0.5 during Power Hour, 1 otherwise). */
export function getPowerHourMultiplier(now: Date = new Date()): number {
  return getPowerHourStatus(now).isActive ? POWER_HOUR_DISCOUNT : 1;
}

/** Discounted wallet cost for a given base duration in seconds, rounded. */
export function discountedWalletCost(seconds: number, now: Date = new Date()): number {
  return Math.round(seconds * getPowerHourMultiplier(now));
}

/**
 * Human-readable time-remaining string for the banner countdown.
 *   2h 14m → "14m" (we only show this when isActive, so < 1h)
 *   6m 20s → "6m 20s"
 *   45s    → "45s"
 */
export function formatPowerHourRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  if (totalSec >= 60) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return s > 0 && m < 10 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${totalSec}s`;
}

/** Long countdown string used when Power Hour is NOT active (hours/mins until it starts). */
export function formatTimeUntil(ms: number): string {
  const totalMin = Math.max(0, Math.ceil(ms / 60000));
  if (totalMin >= 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${totalMin}m`;
}
